import { type ReactNode, type FC } from 'react';
import { TriangleAlert, LoaderCircle } from 'lucide-react';
import { cn } from '../lib/utils';

interface StateMessageProps {
  className?: string;
  title?: string;
  description?: string | ReactNode;
  icon?: FC<{ className: string }>;
  isAbsolute?: boolean;
  isLarge?: boolean;
  isError?: boolean;
  isLoading?: boolean;
}

/**
 * Renders a friendly message to end-user when the UI is some atypical state, for
 * example when a dataset is empty or an error has occurred.
 */
export const StateMessage: FC<StateMessageProps> = ({
  className,
  title,
  description,
  icon: Icon,
  isAbsolute = false,
  isLarge,
  isLoading,
  isError,
}) => {
  const iconSize = isLarge ? 12 : 5;
  let iconContent: ReactNode = null;
  if (Icon) {
    iconContent = (
      <Icon className={`w-${iconSize} h-${iconSize} text-muted-foreground mx-auto mb-4`} />
    );
  } else if (isError) {
    iconContent = (
      <TriangleAlert className={`w-${iconSize} h-${iconSize} text-muted-foreground mx-auto mb-4`} />
    );
  } else if (isLoading) {
    iconContent = (
      <LoaderCircle className={`w-${iconSize} h-${iconSize} mx-auto mb-4 animate-spin`} />
    );
  }
  return (
    <div
      className={cn(
        'mf-state-message flex items-center justify-center',
        !!isAbsolute ? 'absolute inset-0' : 'w-full h-full',
        className
      )}
    >
      <div className="text-center">
        {!!iconContent && iconContent}
        {!!title && <p className="text-muted-foreground mb-4">{title}</p>}
        {!!description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
};
